import { useEffect, useState } from "react";
import { Link } from "react-router-dom";
import { Calendar, ChevronRight, Building2, Clock, Video, FileCheck2 } from "lucide-react";
import { Card, CardContent } from "@/components/ui/card";
import { Progress } from "@/components/ui/progress";
import { Button } from "@/components/ui/button";
import { Skeleton } from "@/components/ui/skeleton";
import { api, formatDateBR, formatDateTimeBR } from "@/lib/api";
import { Logo } from "@/components/Logo";

function RiskBadge({ days }) {
  if (days < 30) {
    return <span className="text-xs px-2 py-0.5 rounded-full bg-red-50 text-red-800 border border-red-200">{days < 0 ? "Vencido" : `${days} dias restantes`}</span>;
  }
  if (days < 60) {
    return <span className="text-xs px-2 py-0.5 rounded-full bg-amber-50 text-amber-900 border border-amber-200">{days} dias restantes</span>;
  }
  return <span className="text-xs px-2 py-0.5 rounded-full bg-emerald-50 text-emerald-800 border border-emerald-200">{days} dias restantes</span>;
}

export default function PortalDashboard() {
  const [data, setData] = useState(null);

  useEffect(() => {
    api.get("/portal/me").then(({ data }) => setData(data));
  }, []);

  if (!data) return <Skeleton className="h-96" />;

  return (
    <div className="space-y-10">
      {/* Hero */}
      <section className="portal-hero-bg rounded-2xl border border-slate-200 px-6 py-10 sm:px-10 sm:py-14">
        <div className="max-w-3xl">
          <div className="text-xs uppercase tracking-[0.2em] text-gold-deep mb-3">
            {data.client?.type === "camara" ? "Câmara Municipal" : "Prefeitura Municipal"}
            {data.client && ` · ${data.client.city}/${data.client.state}`}
          </div>
          <h1 className="serif text-4xl sm:text-5xl font-semibold text-slate-900 leading-tight">
            {data.client?.name}
          </h1>
          <p className="mt-4 text-slate-700 max-w-2xl">
            Portal institucional de acompanhamento dos seus contratos de consultoria com a CEAP Brasil & Lopes.
            Aqui você visualiza o andamento, reuniões, documentos aprovados, atas e prestações de contas mensais.
          </p>
        </div>
      </section>

      {/* Reserved placeholders for future content (cursos, portfólio, notícias) */}
      <section className="grid sm:grid-cols-3 gap-4">
        <div className="border border-dashed border-slate-300 rounded-xl p-6 bg-white/50">
          <div className="text-xs uppercase tracking-wide text-gold-deep font-semibold mb-2">
            Em breve
          </div>
          <h3 className="serif text-lg text-slate-900">Calendário de cursos</h3>
          <p className="text-xs text-slate-600 mt-1">
            Próximas turmas e capacitações da CEAP para gestores municipais.
          </p>
        </div>
        <div className="border border-dashed border-slate-300 rounded-xl p-6 bg-white/50">
          <div className="text-xs uppercase tracking-wide text-gold-deep font-semibold mb-2">
            Em breve
          </div>
          <h3 className="serif text-lg text-slate-900">Portfólio CEAP</h3>
          <p className="text-xs text-slate-600 mt-1">
            Conheça projetos e cases atendidos pela consultoria.
          </p>
        </div>
        <div className="border border-dashed border-slate-300 rounded-xl p-6 bg-white/50">
          <div className="text-xs uppercase tracking-wide text-gold-deep font-semibold mb-2">
            Em breve
          </div>
          <h3 className="serif text-lg text-slate-900">Notícias &amp; conteúdos</h3>
          <p className="text-xs text-slate-600 mt-1">
            Artigos, instruções jurídicas, lives e modelos gratuitos.
          </p>
        </div>
      </section>

      {data.contracts.length === 0 && (
        <Card className="card-soft">
          <CardContent className="py-10 text-center text-sm text-slate-500">
            Nenhum contrato vinculado ao seu município no momento.
          </CardContent>
        </Card>
      )}

      {/* Contracts */}
      <section className="space-y-6">
        <h2 className="serif text-2xl font-semibold text-slate-900">Seus contratos</h2>
        {data.contracts.map((c) => (
          <Card key={c.id} className="card-soft hover:card-elevated transition-shadow">
            <CardContent className="p-6 md:p-8">
              <div className="flex flex-col lg:flex-row justify-between gap-6">
                <div className="flex-1 min-w-0">
                  <div className="text-xs uppercase tracking-wider text-slate-500">Produto contratado</div>
                  <h3 className="serif text-2xl text-slate-900 mt-1">{c.product_name}</h3>
                  <div className="mt-3 flex flex-wrap gap-x-6 gap-y-2 text-sm text-slate-700">
                    <div className="flex items-center gap-1"><Calendar className="w-3.5 h-3.5 text-slate-400" /> Início: {formatDateBR(c.start_date)}</div>
                    <div className="flex items-center gap-1"><Calendar className="w-3.5 h-3.5 text-slate-400" /> Término: {formatDateBR(c.end_date)}</div>
                    <RiskBadge days={c.days_remaining} />
                  </div>
                  {c.product_phases?.length > 0 && (
                    <div className="mt-4">
                      <div className="text-xs text-slate-500 mb-2">
                        Fase atual: <strong className="text-slate-800">{c.product_phases[c.current_phase_index]?.title}</strong>
                      </div>
                      <div className="flex items-center gap-3">
                        <Progress value={c.progress_pct} className="h-2 flex-1" />
                        <span className="text-sm font-semibold text-slate-900">{c.progress_pct}%</span>
                      </div>
                    </div>
                  )}
                </div>

                <div className="lg:w-80 lg:border-l lg:border-slate-200 lg:pl-6 space-y-4">
                  {c.next_meeting ? (
                    <div>
                      <div className="text-xs uppercase tracking-wider text-slate-500 mb-1">Próxima reunião</div>
                      <div className="font-medium text-slate-900">{c.next_meeting.title}</div>
                      <div className="text-sm text-slate-600 flex items-center gap-1 mt-0.5">
                        <Clock className="w-3.5 h-3.5" /> {formatDateTimeBR(c.next_meeting.scheduled_at)}
                      </div>
                      {c.next_meeting.room_url && !c.next_meeting.room_placeholder && (
                        <a href={c.next_meeting.room_url} target="_blank" rel="noreferrer"
                          className="mt-3 inline-flex items-center gap-2 text-xs font-medium px-3 py-1.5 rounded-md bg-midnight text-white">
                          <Video className="w-3.5 h-3.5" /> Entrar na reunião
                        </a>
                      )}
                    </div>
                  ) : (
                    <div>
                      <div className="text-xs uppercase tracking-wider text-slate-500 mb-1">Próxima reunião</div>
                      <div className="text-sm text-slate-500">Sem reuniões agendadas.</div>
                    </div>
                  )}
                  <Link to={`/portal/contratos/${c.id}`}
                    className="inline-flex items-center gap-2 text-sm font-medium text-midnight hover:underline"
                    data-testid={`portal-open-contract-${c.id}`}
                  >
                    Acessar contrato <ChevronRight className="w-4 h-4" />
                  </Link>
                </div>
              </div>
            </CardContent>
          </Card>
        ))}
      </section>
    </div>
  );
}
